// PRD 07 §2.2 #3 / §4.1 — Management Guideline list table.
// Columns: GID · Name · Grade · Linked Item · Status/Save Date
// (Applied PID column removed per PRD 07 v1.7 "목록 '적용된 PID' 컬럼 삭제" — SHCT-369.)
// (+ Delete column, `disabled` without METADATA:WRITE — PRD 07 §5 권한 "버튼은 data-perm 속성 기반
//  disabled 처리" and §4.1 #8 "권한 있을 때만 활성". SHCT-582: the column and the ✕ used to be
//  withheld entirely, which PRD 21 §7.4 P-2 rules out — 버튼을 숨기지 않고 노출 후 비활성.)
// Default sort GID asc; headers in PRD §4.1 are sortable.
//
// Row click opens the edit panel (matches prototype, where the entire row is
// the click target and only the delete button stops propagation). The name
// cell additionally renders with link-text styling per PRD §4.1.

import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import type { ManagementGuideline } from '../types/management-guidelines.types';
import type {
  GuidelineSortKey,
  SortDir,
} from '../store/management-guidelines-page.store';
import { GradeBadge } from './GradeBadge';
import { GuidelineStatusBadge } from './GuidelineStatusBadge';
import { DirtyBadge } from '@/components/feedback/DirtyBadge';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

interface GuidelineTableProps {
  rows: ManagementGuideline[];
  sortKey: GuidelineSortKey;
  sortDir: SortDir;
  onSort: (key: GuidelineSortKey) => void;
  onRowOpen: (row: ManagementGuideline) => void;
  // PRD §7 — 삭제는 METADATA:WRITE 이상. Without it the column still renders and the ✕ is
  // `disabled` + 툴팁 (PRD 21 §7.4 P-2).
  canWrite: boolean;
  onDelete: (row: ManagementGuideline) => void;
}

// Matches the prototype's ↑/↓/↕ glyphs.
function sortIcon(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'asc' ? '↑' : '↓';
}

interface SortableHeaderProps {
  label: string;
  columnKey: GuidelineSortKey;
  sortKey: GuidelineSortKey;
  sortDir: SortDir;
  onSort: (key: GuidelineSortKey) => void;
  style?: React.CSSProperties;
}

function SortableHeader({
  label,
  columnKey,
  sortKey,
  sortDir,
  onSort,
  style,
}: SortableHeaderProps) {
  const active = sortKey === columnKey;
  return (
    <th
      data-sort={columnKey}
      onClick={() => onSort(columnKey)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={style}
    >
      {label} <span className="sort-icon">{sortIcon(active, sortDir)}</span>
    </th>
  );
}

export function GuidelineTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  onRowOpen,
  canWrite,
  onDelete,
}: GuidelineTableProps) {
  const { t } = useTranslation('management-guidelines');
  const { denyProps, tooltip } = useWriteDenyGuard(canWrite);
  // PRD §3.4 — yellow badge next to the GID for a guideline changed since its deployed copy.
  // FEATURE_025 — decided by the server (`row.dirty`); the browser's own list is no longer OR-ed in.
  const isRowDirty = (row: ManagementGuideline) => row.dirty === true;

  // SHCT-378 — long cell content (name / linked item / long PID list) must not
  // overflow and overlap neighbouring columns. Truncate to a single line with an
  // ellipsis; the full value stays reachable via the cell's native title tooltip.
  const ellipsisCell: React.CSSProperties = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    // Fixed layout so a long guideline name stays inside its cell instead of
    // stretching the column and pushing the whole table wider (ticket #7).
    // Every short column is pinned; the unpinned Name column absorbs the rest.
    // Long text columns truncate with an ellipsis + title tooltip (SHCT-378).
    <table className="table table--clickable" style={{ tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <SortableHeader
            label={t('list.columns.gid')}
            columnKey="gid"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            style={{ width: 110 }}
          />
          <SortableHeader
            label={t('list.columns.name')}
            columnKey="name"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          <SortableHeader
            label={t('list.columns.grade')}
            columnKey="grade"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            style={{ width: 80 }}
          />
          <SortableHeader
            label={t('list.columns.linkedItem')}
            columnKey="linkedItem"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            style={{ width: 160 }}
          />
          <SortableHeader
            label={t('list.columns.statusSaveDate')}
            columnKey="deployedAt"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            style={{ width: 140, textAlign: 'center' }}
          />
          <th style={{ width: 60, textAlign: 'center' }}>{t('list.columns.delete')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.gid} onClick={() => onRowOpen(row)}>
            <td>
              {row.gid}
              {isRowDirty(row) ? (
                // SHCT-604 — 3자로 늘어난 문구가 GID 컬럼을 밀지 않도록 줄바꿈을 막는다.
                <DirtyBadge
                  style={{
                    marginLeft: 4,
                    fontSize: 10,
                    padding: '2px 6px',
                    whiteSpace: 'nowrap',
                  }}
                />
              ) : null}
              {/* FEATURE_030 — a guideline whose bound PRO item no longer exists reaches no
                  patient, and until now nothing said so: the patient bundle simply skips it.
                  The server derives this on every read, so the badge cannot outlive the cause. */}
              {row.orphaned === true ? (
                <span
                  className="badge badge-danger"
                  style={{ marginLeft: 4, fontSize: 10, padding: '2px 6px' }}
                  title={t('list.orphanedBadgeHint')}
                >
                  {t('list.orphanedBadge')}
                </span>
              ) : null}
            </td>
            <td style={ellipsisCell} title={row.name}>
              <span className="link-text">{row.name}</span>
            </td>
            <td>
              <GradeBadge grade={row.grade} />
            </td>
            <td style={ellipsisCell} title={row.linkedItem || undefined}>
              {row.linkedItem || '-'}
            </td>
            <td style={{ textAlign: 'center' }}>
              <GuidelineStatusBadge guideline={row} />
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={(e) => {
                  // Prevent the row click from also opening the edit panel.
                  e.stopPropagation();
                  onDelete(row);
                }}
                disabled={!canWrite}
                aria-label={t('list.deleteRow')}
                style={{
                  color: canWrite ? 'var(--destructive)' : undefined,
                  padding: '2px 8px',
                }}
                {...denyProps}
                // The permitted tooltip names the action; without WRITE the §7.4 P-2 copy
                // replaces it, since "why can't I" is the more useful answer of the two.
                title={tooltip ?? t('list.deleteRow')}
              >
                <X size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
